import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { PORTFOLIO_CATEGORIES } from "../lib/categories";
import SEO from "../components/SEO";
import AdSlot from "../components/AdSlot";
import useLocalStorageState from "../hooks/useLocalStorageState";
import useAds from "../hooks/useAds";
import { LayoutGrid, List, ArrowUpRight } from "lucide-react";

const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1761065002677-98e8f9d772a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1770944268023-7709a33e7870?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1763219802762-1d34ee0907c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

function truncate(text, n = 140) {
  if (!text) return "";
  return text.length > n ? text.slice(0, n).trimEnd() + "…" : text;
}

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useLocalStorageState("portfolio_view_mode_v1", "grid"); // "grid" | "list"
  const { hasAds: hasSidebarAds } = useAds("portfolio", "sidebar");

  useEffect(() => {
    api.get("/portfolio")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...PORTFOLIO_CATEGORIES];
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="portfolio-page">
      <SEO
        title="Portfolio — Restaurant Branding Case Studies"
        description="Real restaurants. Real results. See how we transformed Zomato and Swiggy presence for hotels, cafés, cloud kitchens, fine dining and quick-service brands."
        path="/portfolio"
      />
      <section className="border-b border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-sm uppercase tracking-[0.25em] text-[#525252] mb-4">Portfolio</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter leading-none font-black max-w-3xl">
            Real restaurants. <span className="text-[var(--brand-primary)]">Real results.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#525252]">
            A glimpse into how we've helped restaurants & hotels transform their Zomato and Swiggy presence.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Filters + view toggle */}
          <div className="flex flex-wrap items-center gap-3 mb-10">
            <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                  onClick={() => setFilter(c)}
                  className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
                    filter === c
                      ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                      : "bg-white text-[#0A0A0A] border-black/10 hover:border-black/30"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="ml-auto inline-flex border border-black/10 rounded-sm overflow-hidden" data-testid="portfolio-view-toggle" role="tablist" aria-label="View mode">
              <button
                type="button"
                role="tab"
                aria-selected={view === "grid"}
                data-testid="portfolio-view-grid"
                onClick={() => setView("grid")}
                className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 ${view === "grid" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#525252] hover:text-[#0A0A0A]"}`}
                title="Grid view"
              >
                <LayoutGrid size={14}/> Grid
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "list"}
                data-testid="portfolio-view-list"
                onClick={() => setView("list")}
                className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 border-l border-black/10 ${view === "list" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#525252] hover:text-[#0A0A0A]"}`}
                title="List view"
              >
                <List size={14}/> List
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className={hasSidebarAds ? "lg:col-span-9" : "lg:col-span-12"}>
              {loading ? (
                <div data-testid="portfolio-loading" className="text-[#525252]">Loading...</div>
              ) : filtered.length === 0 ? (
                <EmptyShowcase />
              ) : view === "grid" ? (
                <div className={`grid grid-cols-1 md:grid-cols-2 ${hasSidebarAds ? "" : "lg:grid-cols-3"} gap-6`} data-testid="portfolio-grid">
                  {filtered.map((p, idx) => (
                    <div key={p.id} className={`card-reveal card-reveal-${(idx % 6) + 1}`}>
                      <PortfolioCardGrid item={p} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-4" data-testid="portfolio-list">
                  {filtered.map((p, idx) => (
                    <div key={p.id} className={`card-reveal card-reveal-${(idx % 6) + 1}`}>
                      <PortfolioCardList item={p} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            {hasSidebarAds && (
              <aside className="lg:col-span-3" data-testid="portfolio-sidebar-ads">
                <div className="lg:sticky lg:top-24">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#525252] mb-2">Sponsored</div>
                  <AdSlot page="portfolio" slot="sidebar" />
                </div>
              </aside>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function CoverImage({ item, className = "" }) {
  // Prefer the linked restaurant's uploaded image (logo / primary image),
  // then fall back to "after", then "before".
  const id = item.restaurant_logo_image_id || item.after_image_id || item.before_image_id;
  if (!id) {
    return <div className={`bg-[#F0F0F0] ${className}`} aria-hidden />;
  }
  return (
    <img
      src={fileUrl(id)}
      alt={`${item.title} cover`}
      loading="lazy"
      className={`object-cover ${className}`}
    />
  );
}

function PortfolioCardGrid({ item }) {
  return (
    <Link
      to={`/portfolio/${item.slug || item.id}`}
      data-testid={`portfolio-item-${item.id}`}
      className="block bg-white border border-black/5 overflow-hidden group hover:border-[var(--brand-primary)]/30 transition-colors"
    >
      <div className="relative h-56 overflow-hidden bg-[#F0F0F0]">
        <CoverImage item={item} className="w-full h-full group-hover:scale-[1.03] transition-transform duration-500" />
        <div className="absolute top-3 left-3 z-10 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">
          {item.category}
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-display text-xl font-semibold leading-snug">{item.title}</h3>
        {item.description && (
          <p className="mt-2 text-sm text-[#525252] leading-relaxed">{truncate(item.description, 120)}</p>
        )}
        <span
          data-testid={`portfolio-view-more-${item.id}`}
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--brand-primary)] group-hover:gap-2 transition-all"
        >
          View more <ArrowUpRight size={14}/>
        </span>
      </div>
    </Link>
  );
}

function PortfolioCardList({ item }) {
  return (
    <Link
      to={`/portfolio/${item.slug || item.id}`}
      data-testid={`portfolio-item-${item.id}`}
      className="block bg-white border border-black/5 overflow-hidden group hover:border-[var(--brand-primary)]/30 transition-colors"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[260px,1fr]">
        <div className="relative h-44 sm:h-full min-h-[180px] overflow-hidden bg-[#F0F0F0]">
          <CoverImage item={item} className="w-full h-full group-hover:scale-[1.03] transition-transform duration-500" />
          <div className="absolute top-3 left-3 z-10 bg-[var(--brand-primary)] text-white text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm">
            {item.category}
          </div>
        </div>
        <div className="p-6 flex flex-col justify-center">
          <h3 className="font-display text-2xl font-semibold leading-snug">{item.title}</h3>
          {item.description && (
            <p className="mt-2 text-sm text-[#525252] leading-relaxed">{truncate(item.description, 200)}</p>
          )}
          <span
            data-testid={`portfolio-view-more-${item.id}`}
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--brand-primary)] group-hover:gap-2 transition-all"
          >
            View more <ArrowUpRight size={14}/>
          </span>
        </div>
      </div>
    </Link>
  );
}

function EmptyShowcase() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="portfolio-empty-showcase">
      {FALLBACK_IMAGES.map((src) => (
        <div key={src} className="bg-white border border-black/5 overflow-hidden">
          <img src={src} alt="sample" className="w-full h-64 object-cover" />
          <div className="p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--brand-primary)]">Showcase</div>
            <h3 className="mt-2 font-display text-xl font-semibold">Sample Project</h3>
            <p className="mt-2 text-sm text-[#525252]">Portfolio items will appear here once added from the admin dashboard.</p>
          </div>
        </div>
      ))}
    </div>
  );
}
